import { createApi } from "@reduxjs/toolkit/query/react"
import { baseQueryWithAuth } from "~/ui-rtk/utils/base-query"
import type {
  ExperimentCadence,
  ExperimentDataSource,
  ExperimentRecord,
  ExperimentStatus,
} from "~/ui-rtk/types/experiments"

export type CreateExperimentRequest = {
  name: string
  estimatedStartDate?: string
  estimatedEndDate?: string
  channels?: string[]
  countries: string[]
  testPercentage: number
  controlPercentage: number
  durationDays: number
  cadence?: ExperimentCadence
  dataSource?: ExperimentDataSource
  totalBudget?: number
  expectedIroas?: number
}

export type UpdateExperimentRequest = Partial<{
  name: string
  estimatedStartDate: string
  estimatedEndDate: string
  startDate: string
  endDate: string
  channels: string[]
  countries: string[]
  testPercentage: number
  controlPercentage: number
  durationDays: number
  cadence: ExperimentCadence
  totalBudget: number
  expectedIroas: number
  isConfirmed: boolean
  hasResults: boolean
  status: ExperimentStatus
}>

export type ExperimentUploadKind = "HISTORICAL" | "RESULTS"

export type PresignUploadRequest = {
  kind: ExperimentUploadKind
  fileName: string
  fileType: string
}

export type PresignUploadResponse = {
  uploadUrl: string
  s3Key: string
  expiresIn: number
}

export type ConfirmUploadRequest = {
  kind: ExperimentUploadKind
  fileName: string
  s3Key: string
}

export type ExperimentUploadRecord = {
  id: string
  kind: ExperimentUploadKind
  fileName: string
  createdAt: string
}

export type DesignArtifactsResponse = {
  url: string
  expiresIn: number
}

export const experimentApi = createApi({
  reducerPath: "experimentApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["Experiment"],
  endpoints: builder => ({
    getExperiments: builder.query<ExperimentRecord[], void>({
      query: () => ({
        url: "/experiments",
      }),
      providesTags: ["Experiment"],
    }),

    getExperiment: builder.query<ExperimentRecord, string>({
      query: (id: string) => ({
        url: `/experiments/${id}`,
      }),
      providesTags: (_result: unknown, _error: unknown, id: string) => [
        { type: "Experiment", id },
      ],
    }),

    createExperiment: builder.mutation<
      ExperimentRecord,
      CreateExperimentRequest
    >({
      query: (body: CreateExperimentRequest) => ({
        url: "/experiments",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Experiment"],
    }),

    updateExperiment: builder.mutation<
      ExperimentRecord,
      { id: string; body: UpdateExperimentRequest }
    >({
      query: ({ id, body }: { id: string; body: UpdateExperimentRequest }) => ({
        url: `/experiments/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        { id }: { id: string },
      ) => ["Experiment", { type: "Experiment", id }],
    }),

    deleteExperiment: builder.mutation<ExperimentRecord, string>({
      query: (id: string) => ({
        url: `/experiments/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Experiment"],
    }),

    requestDesign: builder.mutation<ExperimentRecord, string>({
      query: (id: string) => ({
        url: `/experiments/${id}/request-design`,
        method: "POST",
      }),
      invalidatesTags: (_result: unknown, _error: unknown, id: string) => [
        "Experiment",
        { type: "Experiment", id },
      ],
    }),

    startExperiment: builder.mutation<ExperimentRecord, string>({
      query: (id: string) => ({
        url: `/experiments/${id}/start`,
        method: "POST",
      }),
      invalidatesTags: (_result: unknown, _error: unknown, id: string) => [
        "Experiment",
        { type: "Experiment", id },
      ],
    }),

    confirmEndExperiment: builder.mutation<
      ExperimentRecord,
      { id: string; endDate?: string }
    >({
      query: ({ id, endDate }: { id: string; endDate?: string }) => ({
        url: `/experiments/${id}/confirm-end`,
        method: "POST",
        body: endDate ? { endDate } : {},
      }),
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        { id }: { id: string },
      ) => ["Experiment", { type: "Experiment", id }],
    }),

    presignExperimentUpload: builder.mutation<
      PresignUploadResponse,
      { id: string; body: PresignUploadRequest }
    >({
      query: ({ id, body }: { id: string; body: PresignUploadRequest }) => ({
        url: `/experiments/${id}/uploads/presign`,
        method: "POST",
        body,
      }),
    }),

    confirmExperimentUpload: builder.mutation<
      ExperimentUploadRecord,
      { id: string; body: ConfirmUploadRequest }
    >({
      query: ({ id, body }: { id: string; body: ConfirmUploadRequest }) => ({
        url: `/experiments/${id}/uploads/confirm`,
        method: "POST",
        body,
      }),
      invalidatesTags: (
        _result: unknown,
        _error: unknown,
        { id }: { id: string },
      ) => [{ type: "Experiment", id }],
    }),

    getExperimentUploads: builder.query<ExperimentUploadRecord[], string>({
      query: (id: string) => ({
        url: `/experiments/${id}/uploads`,
      }),
      providesTags: (_result: unknown, _error: unknown, id: string) => [
        { type: "Experiment", id },
      ],
    }),

    getDesignArtifacts: builder.query<DesignArtifactsResponse, string>({
      query: (id: string) => ({
        url: `/experiments/${id}/design-artifacts`,
      }),
    }),
  }),
})

export const {
  useGetExperimentsQuery,
  useGetExperimentQuery,
  useCreateExperimentMutation,
  useUpdateExperimentMutation,
  useDeleteExperimentMutation,
  useRequestDesignMutation,
  useStartExperimentMutation,
  useConfirmEndExperimentMutation,
  usePresignExperimentUploadMutation,
  useConfirmExperimentUploadMutation,
  useGetExperimentUploadsQuery,
  useLazyGetDesignArtifactsQuery,
} = experimentApi
